// externals
import type { Middleware } from 'redux';
import { nanoid } from 'nanoid';
import { sessionUtils } from '@makemydeal/dr-shared-ui-utils';

// utils
import { pickAllSnapshotSlices } from '../utils/stateSnapshotFieldsAllowlist';
import { computeDelta } from '../utils/stateSnapshotDelta';

// telemetry
import { resolveJoinActionIndex, resolveJoinCorrelationId, nextEmitSequence } from './correlationMiddleware';
import { matchesIgnoredActionPrefix, TELEMETRY_IGNORED_ACTION_PREFIXES } from './telemetryIgnoredActions';

/**
 * Configuration for the state snapshot telemetry middleware.
 *
 * @template TState - The Redux state tree type
 */
export interface StateSnapshotConfig<TState> {
    /**
     * Whether telemetry is enabled. Can be:
     *  - `boolean`: static enable/disable
     *  - `(state) => boolean`: dynamic gate on state (typically a feature toggle
     *    selector). Errors thrown by this function are treated as "disabled".
     *
     * Recommended: gate on `isStateSnapshotTelemetryEnabled(state)` and default
     * to `false` — this middleware is expensive and should be off at bootstrap.
     */
    enabled?: boolean | ((state: TState) => boolean);

    /**
     * Identifier for the Redux store emitting these events (e.g. `'dash-store'`).
     * Emitted on every telemetry event so replay tooling can multi-store correlate.
     */
    storeId: string;

    /**
     * Emit a full-state anchor event (`StateSnapshotAnchor`) every N actions.
     * Deltas alone can be replayed forward from an anchor; without anchors, a
     * missed event would make the rest of the session unrecoverable.
     *
     * Default: `100`. Set to `1` in tests. Anchors are also emitted:
     *  - On the first dispatch after the middleware activates.
     *  - After any dispatch where snapshot capture failed on the previous action.
     *
     * NOTE: A `StateSnapshotDelta` is always emitted for every non-ignored
     * dispatch, including dispatches that also emit an anchor. This gives
     * replay tooling a per-action delta row at every step (no gaps at anchor
     * boundaries) at the cost of one extra low-frequency row per anchor
     * cadence. Anchor and companion delta share `eventId`, `actionIndex`,
     * and `correlationId` so they can be joined 1:1.
     */
    anchorCadence?: number;

    /**
     * Maximum serialized characters for a single PageAction attribute value
     * carrying snapshot data (`snapshotJson`, `deltaJson`, or `chunkPayload`).
     *
     * New Relic Browser silently DROPS any PageAction string custom-attribute
     * value above ~4096 characters. Any anchor or delta whose serialized JSON
     * exceeds this cap is split into N chunk rows sharing a `chunkGroupId`,
     * each carrying `chunkIndex` / `chunkCount` / `chunkPayload`. The replay
     * CLI reassembles chunks by group id before parsing. Un-chunked events
     * (payload fits in one row) preserve the legacy wire shape.
     *
     * Default: `3_000`. Leaves headroom under NR's cap for the other
     * attributes in the PageAction envelope (business context, event ids, etc.).
     * Applies to anchors AND deltas — anchors of the full allowlist snapshot
     * routinely exceed 100 KB and would otherwise be dropped entirely.
     */
    maxChunkBytes?: number;

    /**
     * Extract per-event business context (dealXgId, offerId, sessionId, etc.)
     * from state. Same shape as `correlationMiddleware`. Errors are swallowed.
     */
    getBusinessContext?: (state: TState) => Record<string, unknown>;

    /**
     * Optional readiness gate. When provided, the middleware skips
     * counting `actionIndex` and emitting any telemetry until this predicate
     * returns `true`. Once true it is never called again for the life of the
     * store.
     *
     * Purpose: the `actionIndex === 1` anchor is emitted on the first eligible
     * dispatch after the middleware attaches, which typically happens BEFORE
     * bootstrap has populated the business identifiers (dealXgId, offerId)
     * that per-deal replay tooling filters on. Deferring the counter until
     * business context is ready ensures the anchor carries those identifiers.
     *
     * Called with the pre-reducer state on every dispatch until it returns
     * `true`. Errors are treated as `false` (still waiting).
     */
    waitForReady?: (state: TState) => boolean;

    /**
     * Action-type prefixes to skip. Injectable; defaults to
     * {@link TELEMETRY_IGNORED_ACTION_PREFIXES} (RSAA envelope only). Router /
     * store-init actions ARE snapshotted by default. See telemetryIgnoredActions.
     */
    ignoredActionPrefixes?: readonly string[];
}

/**
 * Event names emitted by this middleware. Kept as constants so downstream
 * NRQL queries and the Python replay CLI (Phase 4) can reference them.
 */
export const STATE_SNAPSHOT_EVENTS = Object.freeze({
    ANCHOR: 'StateSnapshotAnchor',
    DELTA: 'StateSnapshotDelta',
    ERROR: 'StateSnapshotError'
});

const DEFAULT_ANCHOR_CADENCE = 100;
const DEFAULT_MAX_CHUNK_BYTES = 3_000;

// Actions we never snapshot on — same rationale as correlationMiddleware:
// router/redux-api internal noise, plus init/hydration actions where the
// snapshot itself is huge and semantically not a "mutation".
// Match the correlation / selector-delta middleware ignore list to keep the
// event-count budget aligned. Falsy-type policy is LOCAL (tail middlewares
// require a type string, so a type-less action is ignored); the shared prefix
// decision is delegated to `matchesIgnoredActionPrefix` with an injectable set.
function shouldIgnoreAction(actionType: unknown, prefixes: readonly string[]): boolean {
    if (!actionType) return true;
    return matchesIgnoredActionPrefix(String(actionType), prefixes);
}

/**
 * Safely emit a New Relic PageAction. Silent failure — never break the app.
 *
 * The New Relic type definition constrains payload values to primitives
 * (`string | number | boolean`), but our payloads legitimately contain
 * pre-serialized JSON strings alongside numeric fields. All non-primitive
 * values in the payload are already stringified via {@link serializeForNr}
 * upstream — the cast here reflects that invariant.
 */
function emitPageAction(name: string, payload: Record<string, unknown>): void {
    /* istanbul ignore next: window guard for SSR */
    if (typeof window === 'undefined') return;
    try {
        window.newrelic?.addPageAction?.(name, payload as Record<string, string | number>);
    } catch {
        // Silently fail. Telemetry loss is preferred over app breakage.
    }
}

/**
 * Serialize a value for New Relic transport. New Relic PageAction payloads
 * only accept string/number/boolean values, so complex objects are JSON'd.
 * Returns an empty string on serialization failure (e.g. circular refs).
 */
/* istanbul ignore next: catch is defence-in-depth for exotic JSON inputs */
function serializeForNr(value: unknown): string {
    try {
        return JSON.stringify(value);
    } catch {
        return '';
    }
}

/**
 * Emit a snapshot event that may need to be split into chunks to stay under
 * New Relic's per-attribute size limit.
 *
 * @param eventName  PageAction name (e.g. `StateSnapshotAnchor`).
 * @param payloadFieldName  Attribute name used on un-chunked events
 *                          (`snapshotJson` for anchors, `deltaJson` for deltas).
 * @param payloadJson  The serialized payload string.
 * @param sharedAttrs  Attributes present on every row (event id, business
 *                     context, etc.). Duplicated on every chunk so a single
 *                     row remains facetable.
 * @param maxChunkBytes  Per-chunk character cap.
 * @param newChunkGroupId  Factory for the shared chunk group id (only invoked
 *                         when chunking is needed).
 *
 * When `payloadJson.length <= maxChunkBytes` a single row is emitted with the
 * legacy wire shape (`{[payloadFieldName]: payloadJson, ...sharedAttrs}`) —
 * no chunk metadata attributes are added. When chunking is needed, N rows are
 * emitted, each carrying `chunkGroupId`, `chunkIndex`, `chunkCount`, and
 * `chunkPayload` (the raw slice); the legacy `payloadFieldName` attribute is
 * omitted from chunked rows.
 */
function emitPossiblyChunked(
    eventName: string,
    payloadFieldName: string,
    payloadJson: string,
    sharedAttrs: Record<string, unknown>,
    maxChunkBytes: number,
    newChunkGroupId: () => string
): void {
    const cap = Math.max(1, maxChunkBytes);
    if (payloadJson.length <= cap) {
        emitPageAction(eventName, {
            ...sharedAttrs,
            [payloadFieldName]: payloadJson
        });
        return;
    }

    const chunkGroupId = newChunkGroupId();
    const chunkCount = Math.ceil(payloadJson.length / cap);
    for (let chunkIndex = 0; chunkIndex < chunkCount; chunkIndex++) {
        const start = chunkIndex * cap;
        const chunkPayload = payloadJson.slice(start, start + cap);
        emitPageAction(eventName, {
            ...sharedAttrs,
            chunkGroupId,
            chunkIndex,
            chunkCount,
            chunkPayload
        });
    }
}

/**
 * Create a state snapshot telemetry middleware.
 *
 * This middleware captures Redux state before and after each dispatch, then
 * emits telemetry to New Relic as PageActions:
 *
 *  - `StateSnapshotDelta` — emitted for every non-ignored dispatch, carrying
 *    the per-field before/after diff for changed leaves within the allowlist.
 *    This gives replay tooling one delta row per dispatched action, with no
 *    gaps at anchor boundaries.
 *  - `StateSnapshotAnchor` — emitted in ADDITION to the delta at the
 *    configured `anchorCadence` (and on first dispatch / after any error).
 *    Carries the full allowlisted snapshot so replay can seed forward
 *    without needing every intervening delta.
 *
 * The anchor and its companion delta share `eventId`, `actionIndex`, and
 * `correlationId` so downstream tooling can join them 1:1.
 *
 * Emitted event shapes:
 *
 *   StateSnapshotAnchor / StateSnapshotDelta (single-row form, payload fits
 *   under `maxChunkBytes`):
 *     {
 *       eventId, storeId, sessionId, correlationId, actionType,
 *       actionIndex, sizeBytes, changedFieldCount,
 *       snapshotJson | deltaJson: "<serialized payload>",
 *       ...businessContext
 *     }
 *
 *   StateSnapshotAnchor / StateSnapshotDelta (chunked form, one row per
 *   chunk when payload exceeds `maxChunkBytes`):
 *     {
 *       eventId, storeId, sessionId, correlationId, actionType,
 *       actionIndex, sizeBytes, changedFieldCount,
 *       chunkGroupId: "<nanoid shared by all chunks of this event>",
 *       chunkIndex: 0..N-1,
 *       chunkCount: N,
 *       chunkPayload: "<slice of the serialized payload>",
 *       ...businessContext
 *     }
 *
 *   StateSnapshotError: {
 *     eventId, storeId, sessionId, actionType, phase, errorMessage
 *   }
 *
 * @see stateSnapshotFieldsAllowlist for what is included in snapshots.
 * @see stateSnapshotDelta for the delta shape and semantics.
 */
export function createStateSnapshotTelemetryMiddleware<TState = unknown>(
    config: StateSnapshotConfig<TState>
): Middleware<Record<string, never>, TState> {
    const {
        enabled = false,
        storeId,
        anchorCadence = DEFAULT_ANCHOR_CADENCE,
        maxChunkBytes = DEFAULT_MAX_CHUNK_BYTES,
        getBusinessContext,
        waitForReady,
        ignoredActionPrefixes = TELEMETRY_IGNORED_ACTION_PREFIXES
    } = config;

    // Per-store counters. Middleware instances are per-store, so this is safe.
    let actionIndex = 0;
    // Force the next capture to emit an anchor. True on first dispatch and
    // after any capture failure to preserve replay continuity.
    let forceAnchor = true;
    // Readiness latch — once flipped true it stays true. See `waitForReady`
    // config docs. If no `waitForReady` was provided, treated as always ready.
    let isReady = !waitForReady;

    const isEnabled = (state: TState): boolean => {
        try {
            return typeof enabled === 'function' ? enabled(state) : enabled;
        } catch {
            return false;
        }
    };

    const safeBusinessContext = (state: TState): Record<string, unknown> => {
        if (!getBusinessContext) return {};
        try {
            return getBusinessContext(state) ?? {};
        } catch {
            return {};
        }
    };

    return (store) => (next) => (action) => {
        // Fast-path: no-op if disabled. Zero allocations, no state read except
        // the enabled check itself.
        const preState = store.getState();
        if (!isEnabled(preState)) {
            return next(action);
        }

        // Null/undefined actions pass through untouched (matches correlationMiddleware).
        if (!action) {
            return next(action);
        }

        // Skip noisy internal actions.
        if (shouldIgnoreAction((action as { type?: unknown }).type, ignoredActionPrefixes)) {
            return next(action);
        }

        // Readiness gate — skip counting/emitting until business context is
        // populated. See `waitForReady` config docs. This is what makes the
        // eventual actionIndex=1 anchor carry populated business identifiers
        // (dealXgId etc.) so per-deal replay tooling can filter on them.
        if (!isReady) {
            let ready = false;
            try {
                ready = !!waitForReady!(preState);
            } catch {
                ready = false;
            }
            if (!ready) {
                return next(action);
            }
            isReady = true;
        }

        // `shouldIgnoreAction` above rejects any dispatch whose `.type` is
        // falsy, so at this point `.type` is guaranteed truthy.
        const actionType = String((action as { type: unknown }).type);
        const eventId = nanoid();
        const sessionId = safeGetSessionId();
        // Resolve the join keys WITHOUT mutating the action (recommendation #1).
        // Prefer values stamped by correlationMiddleware; for actions that
        // bypassed it (RSAA lifecycle FSAs) resolveJoin* memoizes on this action
        // object so the sibling selectorDelta middleware — which passes the very
        // same action reference through untouched — shares the identical join
        // key instead of drifting on an independent counter. See DE753626.
        const correlationId = resolveJoinCorrelationId(action);
        const emittedActionIndex = resolveJoinActionIndex(store, action);

        // Capture before-state snapshot. Any error here means we skip emitting
        // for this action and force an anchor on the next one.
        let beforeSnapshot: Record<string, unknown>;
        try {
            beforeSnapshot = pickAllSnapshotSlices(preState as Record<string, unknown>);
        } catch (err) {
            emitPageAction(STATE_SNAPSHOT_EVENTS.ERROR, {
                eventId,
                storeId,
                sessionId,
                actionType,
                phase: 'capture-before',
                errorMessage: describeError(err)
            });
            forceAnchor = true;
            return next(action);
        }

        // Run the reducer chain.
        const result = next(action);

        // Capture after-state snapshot.
        let afterSnapshot: Record<string, unknown>;
        try {
            afterSnapshot = pickAllSnapshotSlices(store.getState() as Record<string, unknown>);
        } catch (err) {
            emitPageAction(STATE_SNAPSHOT_EVENTS.ERROR, {
                eventId,
                storeId,
                sessionId,
                actionType,
                phase: 'capture-after',
                errorMessage: describeError(err)
            });
            forceAnchor = true;
            return result;
        }

        // Stamp PRE-reducer business context (from preState) to match
        // correlationMiddleware. Both emitters then denormalize the SAME
        // business identifiers onto every row sharing this actionIndex, so a
        // NRQL facet (e.g. WHERE offerId = X) selects the correlation and
        // snapshot rows for an identifier-mutating dispatch (offer/scenario
        // switch) consistently instead of splitting them across pre/post values.
        const businessContext = safeBusinessContext(preState);
        // Private per-instance dispatch counter drives anchor cadence; it counts
        // ready dispatches THIS middleware observes, independent of the shared
        // join ordinal (emittedActionIndex) resolved before next().
        actionIndex += 1;

        const shouldEmitAnchor = forceAnchor || actionIndex === 1 || actionIndex % Math.max(1, anchorCadence) === 0;

        try {
            // Compute the delta once and reuse it for both the anchor's
            // `c` metadata (and `changedFieldCount`) and the companion
            // StateSnapshotDelta row. Every non-ignored dispatch emits a
            // delta row so replay has a per-action delta at every step,
            // including at anchor boundaries.
            const delta = computeDelta(beforeSnapshot, afterSnapshot);
            const changedFieldCount = Object.keys(delta).length;
            const commonAttrs: Record<string, unknown> = {
                eventId,
                storeId,
                sessionId,
                correlationId,
                actionType,
                actionIndex: emittedActionIndex,
                // Emit-time ordinal (US2056149): tail rows are emitted post-reducer,
                // so emitSequence encodes true reducer-completion order even when it
                // diverges from the initiation-order actionIndex (nested dispatch).
                emitSequence: nextEmitSequence(store),
                ...businessContext
            };

            if (shouldEmitAnchor) {
                const anchorPayload = { s: afterSnapshot, c: delta };
                const snapshotJson = serializeForNr(anchorPayload);
                emitPossiblyChunked(
                    STATE_SNAPSHOT_EVENTS.ANCHOR,
                    'snapshotJson',
                    snapshotJson,
                    {
                        ...commonAttrs,
                        sizeBytes: snapshotJson.length,
                        changedFieldCount
                    },
                    maxChunkBytes,
                    nanoid
                );
                forceAnchor = false;
            }

            // Companion delta — always emitted alongside anchors (and as the
            // sole event for non-anchor dispatches). Zero-change actions are
            // common (thunk-only dispatches, no-op reducers); emit a
            // lightweight "delta with no changes" so replay tooling can
            // count every dispatch, but skip the JSON serialization.
            if (changedFieldCount === 0) {
                emitPageAction(STATE_SNAPSHOT_EVENTS.DELTA, {
                    ...commonAttrs,
                    changedFieldCount: 0,
                    sizeBytes: 2, // "{}"
                    deltaJson: '{}'
                });
            } else {
                const deltaJson = serializeForNr(delta);
                emitPossiblyChunked(
                    STATE_SNAPSHOT_EVENTS.DELTA,
                    'deltaJson',
                    deltaJson,
                    {
                        ...commonAttrs,
                        changedFieldCount,
                        sizeBytes: deltaJson.length
                    },
                    maxChunkBytes,
                    nanoid
                );
            }
        } catch (err) {
            emitPageAction(STATE_SNAPSHOT_EVENTS.ERROR, {
                eventId,
                storeId,
                sessionId,
                actionType,
                phase: 'emit',
                errorMessage: describeError(err)
            });
            forceAnchor = true;
        }

        return result;
    };
}

/* istanbul ignore next: catch is defence-in-depth; getAppSessionId is a pure getter */
function safeGetSessionId(): string {
    try {
        return sessionUtils.getAppSessionId?.() ?? '';
    } catch {
        return '';
    }
}

function describeError(err: unknown): string {
    if (err instanceof Error) return err.message;
    try {
        return String(err);
    } catch {
        return '(unstringifiable error)';
    }
}
